import { Calendar } from "@faire/slate-icons/Calendar";
import { PriceTagDollar } from "@faire/slate-icons/PriceTagDollar";
import { Redo } from "@faire/slate-icons/Redo";
import { Unlock } from "@faire/slate-icons/Unlock";
import type { SVGIconComponent } from "@faire/slate-icons/types";
import { motion, useReducedMotion } from "framer-motion";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";

export const EASE_OUT_EXPO = [0.22, 1, 0.36, 1] as const;

/**
 * Net terms is a genuine perk Faire extends to retailers: buy inventory now,
 * pay up to 60 days later, interest-free. This prototype leans into that as a
 * gift — every string below is written to make the approval feel earned and
 * generous, never like a credit check the retailer has to sweat.
 */
export const NET_TERMS = {
  /** The approved spending limit revealed at the end. Counts up on the reveal. */
  creditLimit: 2000,
  termDays: 60,
  /** Steps the "calculating" loader walks through, one lighting up at a time. */
  calculatingSteps: [
    "Reviewing your store profile",
    "Verifying your business details",
    "Checking your buying power",
    "Setting your payment terms",
  ],
  revealEyebrow: "You're approved",
  revealHeadline: "Congratulations!",
  revealSubhead: "Faire is giving you 60-day payment terms",
  limitLabel: "Your spending limit",
  termBadgeLabel: "Net 60",
  reassurance:
    "This is on us — a head start to stock your shelves now and pay once they're selling.",
  ctaLabel: "Continue",
  homeBannerText: "Net 60 is active — you've got $2,000 to spend, interest-free.",
} as const;

export interface NetTermsBenefit {
  Icon: SVGIconComponent;
  title: string;
  detail: string;
}

/** The three reasons net terms is a good thing, shown as a benefit list on the reveal. */
export const NET_TERMS_BENEFITS: readonly NetTermsBenefit[] = [
  {
    Icon: Calendar,
    title: "Order now, pay in 60 days",
    detail: "Stock up today and settle up long after your shelves are earning.",
  },
  {
    Icon: PriceTagDollar,
    title: "0% interest, always",
    detail: "No fees, no interest — the full 60 days are on the house.",
  },
  {
    Icon: Redo,
    title: "Reorder anytime up to your limit",
    detail: "Your limit refreshes as you pay, so bestsellers never run dry.",
  },
  {
    Icon: Unlock,
    title: "No impact to your credit score",
    detail: "Applying and using your terms never touches your personal credit.",
  },
];

export const formatUsd = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

/** Increments a key to force a full remount so entrance animations replay. */
export const useReplayKey = (): [number, () => void] => {
  const [replayKey, setReplayKey] = useState(0);
  return [replayKey, () => setReplayKey((current) => current + 1)];
};

/**
 * Eases a number from 0 up to `target` over `durationMs` once `active` is true.
 * Uses requestAnimationFrame (not a synchronous effect setState) so it stays
 * clear of the react-hooks set-state-in-effect rule, and collapses to the final
 * value instantly under a reduced-motion preference.
 */
export const useCountUp = (
  target: number,
  durationMs: number,
  active: boolean
): number => {
  const reduceMotion = useReducedMotion() ?? false;
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      setValue(0);
      return;
    }
    if (reduceMotion) {
      setValue(target);
      return;
    }

    let rafId = 0;
    let startTs: number | null = null;
    const tick = (now: number): void => {
      if (startTs === null) {
        startTs = now;
      }
      const progress = Math.min((now - startTs) / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, durationMs, active, reduceMotion]);

  return value;
};

interface RainPiece {
  id: number;
  leftPercent: number;
  delaySeconds: number;
  durationSeconds: number;
  color: string;
  sizePx: number;
  rotateDeg: number;
  driftPx: number;
}

const CONFETTI_COLORS = [
  "#F2B705",
  "#F25C54",
  "#3D7068",
  "#E86A92",
  "#5B8C5A",
  "#1B1B1B",
] as const;

const buildRainConfetti = (count: number): RainPiece[] =>
  Array.from({ length: count }, (_, index) => ({
    id: index,
    leftPercent: Math.random() * 100,
    delaySeconds: Math.random() * 0.5,
    durationSeconds: 2.4 + Math.random() * 1.6,
    color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
    sizePx: 6 + Math.random() * 7,
    rotateDeg: Math.random() * 720 - 360,
    driftPx: (Math.random() - 0.5) * 140,
  }));

/**
 * Decorative confetti rain used by the net-terms reveal. Renders nothing under
 * a reduced-motion preference.
 */
export const Confetti: FunctionComponent<{ pieceCount?: number }> = ({
  pieceCount = 100,
}) => {
  const reduceMotion = useReducedMotion();
  const pieces = useMemo(() => buildRainConfetti(pieceCount), [pieceCount]);

  if (reduceMotion || pieceCount === 0) {
    return null;
  }

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {pieces.map((piece) => (
        <motion.span
          key={piece.id}
          className="absolute top-0 block rounded-[1px]"
          style={{
            left: `${piece.leftPercent}%`,
            width: piece.sizePx,
            height: piece.sizePx * 1.5,
            backgroundColor: piece.color,
          }}
          initial={{ y: "-12vh", opacity: 0, rotate: 0 }}
          animate={{
            y: "116vh",
            x: piece.driftPx,
            rotate: piece.rotateDeg,
            opacity: [0, 1, 1, 0.85],
          }}
          transition={{
            duration: piece.durationSeconds,
            delay: piece.delaySeconds,
            ease: "easeIn",
          }}
        />
      ))}
    </div>
  );
};
